/**
 * Module-level channel through which protected requests tell the microsite's
 * `UserProvider` that the gateway refused the session with a 401.
 *
 * Request code lives in Jotai atoms and plain async helpers that cannot read
 * React context, so the report goes through this bus and the provider is the
 * single subscriber that decides what a rejection means for the visitor.
 */
export type SessionRejectedEvent = {
	/** Identifies the request that observed the 401, for telemetry. */
	source: string;
};

type Listener = (event: SessionRejectedEvent) => void;

const listeners = new Set<Listener>();

/**
 * Reports that a protected request was answered with HTTP 401.
 *
 * Only call this for routes where a 401 means one thing: the visitor's session
 * token is missing, invalid or expired, as Stargate answers before the request
 * reaches the service. Do not call it for a route that returns 401 as part of
 * its own contract while the session is valid, for example to ask for consent,
 * to signal a missing product licence, or to gate a feature. Those are not
 * session failures, and reporting them makes `UserProvider` re-check the
 * session for nothing.
 *
 * The subscriber confirms every report against the gateway before acting on
 * it, so a mistaken call cannot log a visitor out by itself, but the contract
 * above is what keeps that check from running on every ordinary 401.
 *
 * @param source A short, stable name for the request, for telemetry.
 */
export const reportSessionRejected = (source: string): void => {
	const event: SessionRejectedEvent = { source };
	listeners.forEach((listener) => listener(event));
};

export const subscribeToSessionRejections = (listener: Listener): (() => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};
